'use client';

import { EnvelopeIcon } from '@phosphor-icons/react/ssr';
import { useState } from 'react';

import { Logo } from '@/components/brand/logo';
import { authClient } from '@/lib/auth-client';
import { getPostHog } from '@/lib/posthog-client';
import { cn } from '@/lib/utils';

type State =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; email: string }
  | { kind: 'error'; message: string };

export const Login = () => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;

    setState({ kind: 'sending' });
    const trimmed = email.trim();
    const { error } = await authClient.signIn.magicLink({
      email: trimmed,
      callbackURL: '/',
    });

    if (error) {
      setState({
        kind: 'error',
        message: error.message ?? 'Could not send sign-in link',
      });
      return;
    }

    const at = trimmed.lastIndexOf('@');
    const domain = at >= 0 ? trimmed.slice(at + 1).toLowerCase() : '';
    getPostHog()?.capture('creator_magic_link_requested', {
      email_domain: domain,
    });

    setState({ kind: 'sent', email: trimmed });
  };

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] px-6 py-10 font-sans text-[var(--fg)] sm:py-12">
      <div className="mx-auto grid min-h-[calc(100vh-6rem)] w-full max-w-[920px] items-center gap-8 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div>
          <a href="/" className="inline-flex">
            <Logo wordmarkSize={20} wordmarkClassName="text-[var(--fg)]" />
          </a>

          <div className="mt-10 max-w-[52ch]">
            <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--accent-text)]">
              sign in
            </div>
            <h1 className="mt-3 font-display text-[40px] font-medium leading-[1.02] tracking-[-0.03em] text-[var(--fg)] text-balance">
              Sign in to Lore.
            </h1>
            <p className="mt-4 text-pretty text-[16px] leading-[1.7] text-[var(--fg-muted)]">
              Enter your email and we’ll send a secure link to your dashboard.
              No password needed.
            </p>
            <p className="mt-5 text-[13px] leading-[1.6] text-[var(--fg-subtle)]">
              New to Lore?{' '}
              <a href="/" className="underline underline-offset-2">
                Learn how collections work
              </a>
              .
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--hairline)] bg-[var(--bg-raised)]">
          <div className="border-b border-[var(--hairline)] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--fg-subtle)]">
            authentication
          </div>
          <div className="px-5 py-5">
            {state.kind === 'sent' ? (
              <div>
                <h1 className="font-display text-[28px] font-medium tracking-[-0.02em] text-[var(--fg)]">
                  Check your inbox.
                </h1>
                <p className="mt-3 text-[14px] leading-[1.7] text-[var(--fg-muted)]">
                  We sent a sign-in link to{' '}
                  <span className="font-medium text-[var(--fg)]">
                    {state.email}
                  </span>
                  . It expires in 15 minutes.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h1 className="font-display text-[28px] font-medium leading-[1.08] tracking-[-0.02em] text-[var(--fg)]">
                    Enter your email.
                  </h1>
                  <p className="mt-2 text-[14px] leading-[1.65] text-[var(--fg-muted)]">
                    We’ll send a short-lived sign-in link for your Lore account.
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  <label
                    htmlFor="email"
                    className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--fg-muted)]"
                  >
                    your email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={state.kind === 'sending'}
                    className={cn(
                      'min-h-11 rounded-[calc(var(--radius-lg)-2px)] border px-3 text-[14px] font-sans',
                      'border-[var(--hairline)] bg-[var(--bg-elevated)] text-[var(--fg)] placeholder:text-[var(--fg-subtle)]',
                      'focus:border-[var(--accent-text)] focus:outline-none focus:ring-2 focus:ring-[color-mix(in_oklab,var(--accent-text)_18%,transparent)]',
                      'transition-[background-color,border-color,box-shadow] duration-[120ms] ease-[var(--ease-out)]'
                    )}
                  />
                  {state.kind === 'error' && (
                    <p className="rounded-[calc(var(--radius-lg)-2px)] border border-[color-mix(in_oklab,var(--destructive)_38%,transparent)] bg-[color-mix(in_oklab,var(--destructive)_12%,transparent)] px-3 py-2 text-[13px] text-[var(--destructive)]">
                      {state.message}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={state.kind === 'sending' || !email.trim()}
                    className={cn(
                      'inline-flex min-h-11 items-center justify-center gap-2 rounded-[calc(var(--radius-lg)-2px)] border border-[var(--accent-text)] px-4 text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--accent-text)] mt-1',
                      'transition-[transform,box-shadow,background-color,opacity] duration-[120ms] ease-[var(--ease-out)]',
                      'hover:bg-[color-mix(in_oklab,var(--accent-text)_10%,transparent)] hover:shadow-[var(--glow-signal)]',
                      'active:scale-[0.96]',
                      'disabled:opacity-60 disabled:hover:bg-transparent disabled:hover:shadow-none'
                    )}
                  >
                    <EnvelopeIcon className="w-4 h-4" />
                    {state.kind === 'sending'
                      ? 'sending…'
                      : 'send sign-in link'}
                  </button>
                  <p className="mt-2 text-[12px] leading-[1.6] text-[var(--fg-subtle)]">
                    by continuing you agree to our{' '}
                    <a href="/tos" className="underline underline-offset-2">
                      terms
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" className="underline underline-offset-2">
                      privacy policy
                    </a>
                    .
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
